import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { 
  CheckCircle, 
  MapPin, 
  Calendar, 
  Clock, 
  Car, 
  Users, 
  Briefcase,
  Moon,
  ArrowRight,
  Phone,
  Mail,
  Home,
  CreditCard,
  Timer,
  Ship
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useThemeTexts } from "../components/ThemeProvider";

// Live countdown for "Pay to Lock It". Updates every second.
// Returns null after expiry so the parent can swap the urgency UI for neutral.
function PaymentHoldCountdown({ expiresAt }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const target = new Date(expiresAt).getTime();
  const remaining = Math.max(0, Math.floor((target - now) / 1000));
  if (remaining <= 0) return null;
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  return (
    <div
      className="mt-3 flex items-center justify-center gap-2 text-[#C5A059] text-sm font-mono tracking-wider"
      data-testid="payment-hold-countdown"
      aria-live="polite"
    >
      <Timer className="w-4 h-4" />
      <span>Rate held for <strong className="font-bold">{mm}:{ss}</strong></span>
    </div>
  );
}

export default function QuoteConfirmation() {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const t = useThemeTexts();

  const fetchQuote = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}`);
      setQuote(response.data);
    } catch (error) {
      console.warn("Quote not found", quoteId, error?.response?.status);
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/50 mb-4">Quote not found</p>
          <Link to="/">
            <Button className="btn-gold">Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const awaitingPayment = quote.status === "awaiting_payment";

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full" data-testid="quote-confirmation">
        {/* Success Animation */}
        <div className="text-center mb-8 animate-fade-in-up">
          <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${awaitingPayment ? "bg-[#C5A059]/15" : "bg-emerald-500/10"}`}>
            {awaitingPayment
              ? <CreditCard className="w-10 h-10 text-[#C5A059]" />
              : <CheckCircle className="w-10 h-10 text-emerald-400" />}
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {awaitingPayment ? "Booking Confirmed — Pay to Lock It" : "Quote Received!"}
          </h1>
          <p className="text-white/50">
            {awaitingPayment
              ? "Your transfer is on hold. Pay now to confirm the driver and receive the booking voucher."
              : "Thank you for your request. Our team will contact you shortly."}
          </p>
        </div>

        {/* Inline Pay-Now CTA (visible for awaiting_payment quotes) */}
        {awaitingPayment && (
          <div
            className="mb-6 p-5 rounded-sm border border-[#C5A059]/40 bg-[#C5A059]/8 animate-fade-in-up animate-delay-100"
            data-testid="pay-now-banner"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-white/60 text-xs uppercase tracking-wide mb-1">Amount due</p>
                <p className="text-2xl font-bold text-[#C5A059] font-mono">€{quote.total_price}</p>
              </div>
              <Link to={`/pay/${quote.quote_id}`} data-testid="pay-now-link">
                <Button className="btn-gold px-6 py-3 h-auto text-base font-semibold tracking-wide">
                  <CreditCard className="w-5 h-5 mr-2" /> PAY NOW
                </Button>
              </Link>
            </div>
            {quote.pay_hold_until && <PaymentHoldCountdown expiresAt={quote.pay_hold_until} />}
            <p className="text-white/40 text-[11px] mt-3">
              Secure payment via Stripe — Visa, Mastercard, Apple Pay, Google Pay accepted.
            </p>
          </div>
        )}

        {/* Quote Details Card */}
        <div className="booking-widget rounded-sm p-6 animate-fade-in-up animate-delay-200" data-testid="quote-details">
          <div className="text-center mb-6 pb-6 border-b border-white/10">
            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Quote Reference</p>
            <p className="text-[#C5A059] font-mono text-lg">{quote.quote_id}</p>
          </div>

          {/* Route */}
          <div className="mb-6">
            {quote.venice_hotel_name && (
              <div className="flex items-center gap-1.5 mb-2 px-3 py-2 rounded-sm bg-[#0F0F0F] border-l-2 border-[#C5A059]" data-testid="quote-hotel-line">
                <Ship className="w-4 h-4 text-[#C5A059]" />
                <span className="text-[#C5A059] font-medium text-sm">{quote.venice_hotel_name}</span>
                <span className="text-white/40 text-xs">· water taxi + meet &amp; greet included</span>
              </div>
            )}
            <div className="flex items-center justify-between text-white mb-2">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#C5A059]" />
                <span className="text-sm">{quote.origin}</span>
              </div>
              <ArrowRight className="w-4 h-4 text-white/30" />
              <span className="text-sm">{quote.destination}</span>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-[#1F1F1F]/50 rounded-sm p-3">
              <div className="flex items-center gap-2 text-white/40 text-xs mb-1">
                <Calendar className="w-3 h-3" />
                Date
              </div>
              <p className="text-white text-sm">{quote.pickup_date}</p>
            </div>
            <div className="bg-[#1F1F1F]/50 rounded-sm p-3">
              <div className="flex items-center gap-2 text-white/40 text-xs mb-1">
                <Clock className="w-3 h-3" />
                Time
              </div>
              <p className="text-white text-sm flex items-center gap-2">
                {quote.pickup_time}
                {quote.is_night_service && <Moon className="w-3 h-3 text-[#C5A059]" />}
              </p>
            </div>
            <div className="bg-[#1F1F1F]/50 rounded-sm p-3">
              <div className="flex items-center gap-2 text-white/40 text-xs mb-1">
                <Users className="w-3 h-3" />
                Passengers
              </div>
              <p className="text-white text-sm">{quote.passengers}</p>
            </div>
            <div className="bg-[#1F1F1F]/50 rounded-sm p-3">
              <div className="flex items-center gap-2 text-white/40 text-xs mb-1">
                <Briefcase className="w-3 h-3" />
                Luggage
              </div>
              <p className="text-white text-sm">{quote.luggage} bags</p>
            </div>
          </div>

          {/* Vehicle */}
          <div className="bg-[#1F1F1F]/50 rounded-sm p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Car className="w-5 h-5 text-[#C5A059]" />
                <span className="text-white">{quote.vehicle_class}</span>
              </div>
            </div>
          </div>

          {/* Price */}
          <div className="border-t border-white/10 pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/50">Base Price</span>
              <span className="text-white font-mono">€{quote.base_price}</span>
            </div>
            {quote.is_night_service && (
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/50 flex items-center gap-1">
                  <Moon className="w-3 h-3 text-[#C5A059]" />
                  Night Surcharge
                </span>
                <span className="text-white font-mono">€{quote.night_surcharge}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <span className="text-white font-semibold">Estimated Total</span>
              <span className="text-2xl font-bold text-[#C5A059] font-mono">€{quote.total_price}</span>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-6 text-center text-white/40 text-sm animate-fade-in-up animate-delay-300">
          <p className="mb-4">Questions about your booking?</p>
          <div className="flex justify-center gap-6">
            <a href={`tel:${(t.footer_contact_phone || "").replace(/\s+/g, "")}`} className="flex items-center gap-2 hover:text-[#C5A059] transition-colors">
              <Phone className="w-4 h-4" />
              {t.footer_contact_phone}
            </a>
            <a href={`mailto:${t.footer_contact_email}`} className="flex items-center gap-2 hover:text-[#C5A059] transition-colors">
              <Mail className="w-4 h-4" />
              Email Us
            </a>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 animate-fade-in-up animate-delay-400">
          <Link to="/" className="flex-1">
            <Button className="btn-outline-gold w-full">
              <Home className="w-4 h-4 mr-2" />
              Return Home
            </Button>
          </Link>
          {awaitingPayment ? (
            <Link to={`/pay/${quote.quote_id}`} className="flex-1" data-testid="pay-now-link-bottom">
              <Button className="btn-gold w-full">
                <CreditCard className="w-4 h-4 mr-2" />
                Pay €{quote.total_price}
              </Button>
            </Link>
          ) : (
            <Link to="/#booking" className="flex-1">
              <Button className="btn-gold w-full">
                Book Another Transfer
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
